import React, { useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilExternalLink, cilX } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import { ImportErrorAlert, InfoBox } from 'src/components/common/ImportWizard'
import PlayHQService from 'src/service/PlayHQService'
import LinkImporter from './LinkImporter'
import FixturesImporter from './FixturesImporter'
import RoundsImporter from './RoundsImporter'
import ScoresImporter from './ScoresImporter'
import { gradeConfig, playerConfig, teamConfig } from './linkConfigs'
import { LoadingBlock, errorsOf } from './playhqShared'

const TABS = [
  { key: 'grades', label: 'Grades' },
  { key: 'teams', label: 'Teams' },
  { key: 'players', label: 'Players' },
  { key: 'rounds', label: 'Rounds' },
  { key: 'fixtures', label: 'Fixtures' },
  { key: 'scores', label: 'Squads & Scores' },
]

/**
 * PlayHQ Import. The game links to its club's play.cricket page once; after that the admin
 * picks a season (or pastes a grade or match link to work on just that) and imports grades,
 * teams, players, fixtures, and squads with scores - each on its own, in any order.
 */
const PlayHQImport = () => {
  const [info, setInfo] = useState(null)
  const [loadError, setLoadError] = useState(null)

  // club link
  const [editingLink, setEditingLink] = useState(false)
  const [clubUrl, setClubUrl] = useState('')
  const [linkErrors, setLinkErrors] = useState([])
  const [savingLink, setSavingLink] = useState(false)

  // scope
  const [seasonId, setSeasonId] = useState('')
  const [linkScope, setLinkScope] = useState(null) // { scope, teams }
  const [scopeUrl, setScopeUrl] = useState('')
  const [scopeErrors, setScopeErrors] = useState([])
  const [resolving, setResolving] = useState(false)
  const [scopeTeams, setScopeTeams] = useState([])
  const [teamFilter, setTeamFilter] = useState('')

  // Each tab mounts afresh when opened, so it always reads what the other tabs just imported.
  const [tab, setTab] = useState('grades')

  const load = () => {
    setLoadError(null)
    PlayHQService.getLink()
      .then((res) => {
        if (!res.success) {
          setLoadError(errorsOf(res)[0])
          return
        }
        setInfo(res.data)
        const current = (res.data.seasons || []).find((s) => s.is_current) || res.data.seasons?.[0]
        setSeasonId((id) => id || current?.id || '')
      })
      .catch(() => setLoadError('This page could not be loaded. Please refresh to try again.'))
  }

  useEffect(load, [])

  const scope = useMemo(() => {
    if (linkScope) return linkScope.scope
    return seasonId ? { type: 'season', id: seasonId } : null
  }, [linkScope, seasonId])

  // Our club's teams in the scope, for the team filter.
  useEffect(() => {
    setTeamFilter('')
    if (!scope) {
      setScopeTeams([])
      return
    }
    if (linkScope) {
      setScopeTeams(linkScope.teams)
      return
    }
    setScopeTeams([])
    PlayHQService.preview('teams', { scope })
      .then((res) => setScopeTeams(res.success ? res.data.items : []))
      .catch(() => setScopeTeams([]))
  }, [scope, linkScope])

  const teamIds = useMemo(() => (teamFilter ? [teamFilter] : []), [teamFilter])

  const saveLink = async () => {
    setSavingLink(true)
    setLinkErrors([])
    try {
      const res = await PlayHQService.saveLink(clubUrl.trim())
      if (!res.success) {
        setLinkErrors(errorsOf(res))
        return
      }
      ToastComponent(res.message, 'success')
      setEditingLink(false)
      setClubUrl('')
      setLinkScope(null)
      setSeasonId('')
      setInfo((current) => ({
        ...current,
        link: res.data.link,
        seasons: res.data.seasons,
        season_error: null,
      }))
      const season = res.data.seasons.find((s) => s.is_current) || res.data.seasons[0]
      setSeasonId(season?.id || '')
    } catch (e) {
      setLinkErrors(['The club could not be linked. Please try again.'])
    } finally {
      setSavingLink(false)
    }
  }

  const removeLink = async () => {
    if (
      !window.confirm('Remove the PlayHQ club link? Records you already imported stay as they are.')
    )
      return
    const res = await PlayHQService.removeLink()
    if (res.success) {
      ToastComponent(res.message, 'success')
      setInfo((current) => ({ ...current, link: null, seasons: [] }))
      setLinkScope(null)
      setSeasonId('')
    }
  }

  const resolveScope = async () => {
    setResolving(true)
    setScopeErrors([])
    try {
      const res = await PlayHQService.resolveScope(scopeUrl.trim())
      if (!res.success) {
        setScopeErrors(errorsOf(res))
        return
      }
      setLinkScope(res.data)
      setScopeUrl('')
      if (res.data.scope.type === 'match' && (tab === 'grades' || tab === 'teams')) setTab('scores')
    } catch (e) {
      setScopeErrors(['That link could not be read. Please try again.'])
    } finally {
      setResolving(false)
    }
  }

  const header = (
    <div className="mb-3">
      <h4 className="fw-bold mb-0">PlayHQ Import</h4>
      <div className="small text-medium-emphasis">
        Pull grades, teams, players, rounds, fixtures, squads and scores from play.cricket.com.au.
      </div>
    </div>
  )

  if (loadError) {
    return (
      <>
        {header}
        <ImportErrorAlert errors={[loadError]} title="Something went wrong:" />
      </>
    )
  }
  if (!info) return <LoadingBlock text="Loading…" />

  if (!info.supported) {
    return (
      <>
        {header}
        <InfoBox color="light">PlayHQ import is available for club games.</InfoBox>
      </>
    )
  }

  const link = info.link

  const linkForm = (
    <CCard className="mb-4 border-0 shadow-sm">
      <CCardBody>
        <h5 className="fw-semibold mb-1">
          {link ? 'Change your PlayHQ club' : 'Link your PlayHQ club'}
        </h5>
        <div className="small text-medium-emphasis mb-3">
          Search for your club on{' '}
          <a href="https://play.cricket.com.au" target="_blank" rel="noreferrer">
            play.cricket.com.au
          </a>
          , open it, and copy the address from your browser. You only need to do this once.
        </div>
        <CRow className="g-2 align-items-start">
          <CCol md={9}>
            <CFormInput
              value={clubUrl}
              onChange={(e) => setClubUrl(e.target.value)}
              placeholder="https://play.cricket.com.au/club/your-club/8de680a0-…"
              onKeyDown={(e) => e.key === 'Enter' && clubUrl.trim() && saveLink()}
            />
          </CCol>
          <CCol md={3} className="d-flex gap-2">
            <CLoadingButton
              className="text-nowrap"
              color="primary"
              loading={savingLink}
              disabled={!clubUrl.trim()}
              onClick={saveLink}
            >
              Link club
            </CLoadingButton>
            {link && (
              <CButton
                className="text-nowrap"
                color="secondary"
                variant="outline"
                onClick={() => setEditingLink(false)}
              >
                Cancel
              </CButton>
            )}
          </CCol>
        </CRow>
        <div className="mt-3">
          <ImportErrorAlert errors={linkErrors} title="The club could not be linked:" />
        </div>
      </CCardBody>
    </CCard>
  )

  if (!link || editingLink) {
    return (
      <>
        {header}
        {linkForm}
      </>
    )
  }

  const seasons = info.seasons || []
  const selectedTeam = scopeTeams.find((t) => t.playhq_id === teamFilter)

  return (
    <>
      {header}
      <CCard className="mb-3 border-0 shadow-sm">
        <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            {link.org_logo && (
              <img
                src={link.org_logo}
                alt=""
                style={{ width: 48, height: 48, objectFit: 'contain' }}
              />
            )}
            <div>
              <div className="fw-semibold">{link.org_name}</div>
              <a href={link.club_url} target="_blank" rel="noreferrer" className="small">
                View on play.cricket <CIcon icon={cilExternalLink} size="sm" />
              </a>
            </div>
          </div>
          <div className="d-flex gap-2">
            <CButton
              size="sm"
              color="secondary"
              variant="outline"
              onClick={() => setEditingLink(true)}
            >
              Change club
            </CButton>
            <CButton size="sm" color="danger" variant="ghost" onClick={removeLink}>
              Remove link
            </CButton>
          </div>
        </CCardBody>
      </CCard>

      <CCard className="mb-3 border-0 shadow-sm">
        <CCardBody>
          {info.season_error && (
            <ImportErrorAlert errors={[info.season_error]} title="Seasons could not be loaded:" />
          )}
          <CRow className="g-3">
            <CCol lg={4}>
              <CFormLabel className="small fw-semibold">Season</CFormLabel>
              {linkScope ? (
                <div className="d-flex align-items-center gap-2 border rounded px-2 py-1">
                  <span className="small text-truncate">{linkScope.scope.label}</span>
                  <CButton
                    size="sm"
                    color="secondary"
                    variant="ghost"
                    className="ms-auto"
                    onClick={() => setLinkScope(null)}
                    title="Back to seasons"
                  >
                    <CIcon icon={cilX} size="sm" />
                  </CButton>
                </div>
              ) : (
                <CFormSelect value={seasonId} onChange={(e) => setSeasonId(e.target.value)}>
                  {seasons.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {s.is_current ? ' (current)' : ''}
                    </option>
                  ))}
                </CFormSelect>
              )}
            </CCol>
            <CCol lg={3}>
              <CFormLabel className="small fw-semibold">Team</CFormLabel>
              <CFormSelect
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                disabled={scopeTeams.length < 2}
              >
                <option value="">All teams</option>
                {scopeTeams.map((t) => (
                  <option key={t.playhq_id} value={t.playhq_id}>
                    {t.name}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol lg={5}>
              <CFormLabel className="small fw-semibold">
                Or just one grade or match (optional)
              </CFormLabel>
              <div className="d-flex gap-2">
                <CFormInput
                  className="flex-grow-1"
                  style={{ minWidth: 0 }}
                  value={scopeUrl}
                  onChange={(e) => setScopeUrl(e.target.value)}
                  placeholder="Paste a play.cricket grade or match link"
                  onKeyDown={(e) => e.key === 'Enter' && scopeUrl.trim() && resolveScope()}
                />
                <CLoadingButton
                  className="flex-shrink-0 text-nowrap"
                  color="primary"
                  variant="outline"
                  loading={resolving}
                  disabled={!scopeUrl.trim()}
                  onClick={resolveScope}
                >
                  Use
                </CLoadingButton>
              </div>
            </CCol>
          </CRow>
          <div className="mt-2">
            <ImportErrorAlert errors={scopeErrors} title="That link could not be used:" />
          </div>
        </CCardBody>
      </CCard>

      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody>
          <CNav variant="underline" className="mb-4 flex-nowrap overflow-auto">
            {TABS.map((t) => (
              <CNavItem key={t.key}>
                <CNavLink
                  active={tab === t.key}
                  onClick={() => setTab(t.key)}
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {t.label}
                </CNavLink>
              </CNavItem>
            ))}
          </CNav>
          {selectedTeam &&
            (tab === 'players' || tab === 'rounds' || tab === 'fixtures' || tab === 'scores') && (
              <div className="small text-medium-emphasis mb-3">
                Showing {selectedTeam.name} only.
              </div>
            )}
          {!scope ? (
            <InfoBox color="light">Choose a season, or paste a grade or match link.</InfoBox>
          ) : (
            <Importer
              key={`${tab}:${scope.type}:${scope.id}:${teamFilter}`}
              tab={tab}
              scope={scope}
              teamIds={teamIds}
            />
          )}
        </CCardBody>
      </CCard>
    </>
  )
}

// Grades and teams are about the scope, not one team, so the team filter does not apply to them.
const NO_TEAMS = []
const Importer = ({ tab, scope, teamIds }) => {
  if (tab === 'grades')
    return <LinkImporter type="grades" scope={scope} teamIds={NO_TEAMS} config={gradeConfig} />
  if (tab === 'teams')
    return <LinkImporter type="teams" scope={scope} teamIds={NO_TEAMS} config={teamConfig} />
  if (tab === 'players')
    return <LinkImporter type="players" scope={scope} teamIds={teamIds} config={playerConfig} />
  if (tab === 'rounds') return <RoundsImporter scope={scope} teamIds={teamIds} />
  if (tab === 'fixtures') return <FixturesImporter scope={scope} teamIds={teamIds} />
  return <ScoresImporter scope={scope} teamIds={teamIds} />
}

export default PlayHQImport
